import { useEffect } from "react";

/**
 * Injects a JSON-LD <script> into <head> for the lifetime of the component,
 * keyed by `id`. Used for per-page structured data (e.g. FAQPage) so it is
 * present after client-side navigation. On first load / for non-JS crawlers,
 * the same JSON-LD is baked into the static HTML by scripts/seo/prerender.mjs.
 *
 * Pass `null` to skip injection.
 */
export function useJsonLd(id: string, data: object | null) {
  // Serialize once so the effect only re-runs when the content changes.
  const json = data ? JSON.stringify(data) : null;

  useEffect(() => {
    if (!json) return;

    let el = document.getElementById(id) as HTMLScriptElement | null;
    if (!el) {
      el = document.createElement("script");
      el.type = "application/ld+json";
      el.id = id;
      document.head.appendChild(el);
    }
    el.textContent = json;

    return () => {
      document.getElementById(id)?.remove();
    };
  }, [id, json]);
}
